<template>
  <!-- Reusable consumption-cost chart. Pivots long-form
       (day × entity × cost) rows into top-N stacked series with the
       remainder bucketed into "Other". Supports curves / lines / bars
       (+ filled, stacked, data labels, top-N, CSV export) via the
       shared chart-options menu, and the legend supports double-click
       to isolate. Used by:
         - /dashboards/overview             (entity = cost category)
         - /access-control/user-management  (entity = user)
         - /access-control/role-management  (entity = role)
       All pages pass their own title/description and the rows
       returned by their respective *OverTimeSql query (or, on the
       overview, a pivot of the wide-form trend payload).

       Root is `h-full flex flex-col` so when this component lives in a
       stretching grid row (e.g. the overview's Cost Trend ↔ Cost By
       Warehouse pair) it fills the row height and the chart area
       expands; when the parent has no defined height (the access-
       control pages) `h-full` resolves to auto and the chart sticks at
       its natural 18rem floor. Same trick used by CostByWarehouse on
       the overview page. -->
       <!-- (Arbitrary-value Tailwind: `min-h-[18rem]` rather than
            `min-h-72` because TW 3.4 only ships min-h-0/full/screen.) -->
  <div class="glass-panel p-6 h-full flex flex-col relative">
    <!-- Header — title + description on the left, with the title
         block reserving right-side padding (`pr-44`) so its text
         never runs underneath the corner-pinned controls. -->
    <div class="mb-4 pr-44">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <svg class="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 13l4-4 4 4 4-8 4 8" /></svg>
        <span>{{ title }}</span>
      </h2>
      <p class="text-xs text-gray-500 mt-0.5">
        {{ description }}
        Top {{ topN }} {{ entityLabelPlural }} shown by total; the rest bucketed as "Other".
        <span class="ml-1 text-gray-400">· Click a legend entry to toggle, double-click to isolate.</span>
      </p>
    </div>

    <!-- Top-right corner — total in range + options dropdown. Pinned
         to the panel via absolute positioning so they stay in the
         corner no matter how the title wraps or how wide the
         viewport is. The reserved `pr-44` on the header above keeps
         the title text from running under this block. -->
    <div class="absolute top-6 right-6 flex items-center gap-3">
      <div class="flex items-center gap-2 text-xs whitespace-nowrap">
        <span class="text-gray-500">Total in range:</span>
        <span class="font-bold text-gray-900 dark:text-white text-base">${{ totalInRange.toFixed(2) }}</span>
      </div>
      <ChartTypeMenu
        v-model="chartType"
        :stacked="stacked"
        :filled="filled"
        :data-labels="dataLabels"
        :top-n="topN"
        :can-export="props.rows.length > 0"
        @update:stacked="stacked = $event"
        @update:filled="filled = $event"
        @update:dataLabels="dataLabels = $event"
        @update:topN="topN = $event"
        @export="exportCsv"
      />
    </div>

    <!-- Chart / loading / empty states use `flex-1 min-h-[18rem]` so they
         fill any extra row height when the panel is stretched, while
         keeping a 288px floor for legibility on un-stretched pages.
         Loading uses the branded PulsarLoader so the access-control
         pages (user / role consumption charts) and the overview's
         Cost Trend all read with the same kinetic identity while
         data fetches. -->
    <div v-if="loading" class="flex-1 min-h-[18rem] flex items-center justify-center">
      <PulsarLoader :label="`Loading ${entityLabelSingular}-cost trend…`" size="md" />
    </div>
    <div v-else-if="!chartData" class="flex-1 min-h-[18rem] flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
      No spend recorded in this range.
    </div>
    <div v-else class="flex-1 min-h-[18rem]">
      <Line
        v-if="chartType !== 'bar'"
        :data="chartData"
        :options="chartOptions"
        :plugins="[dataLabelsPlugin]"
      />
      <Bar
        v-else
        :data="chartData"
        :options="chartOptions"
        :plugins="[dataLabelsPlugin]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Line, Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import ChartTypeMenu from '~/components/dashboard/ChartTypeMenu.vue'
import PulsarLoader from '~/components/common/PulsarLoader.vue'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

/** Long-form row from a *OverTimeSql query — `entity` is the
 *  pivot dimension (user, role, warehouse, …). */
export interface ConsumptionCostRow {
  day: string
  entity: string
  cost: number
}

const props = withDefaults(defineProps<{
  rows: ConsumptionCostRow[]
  loading?: boolean
  /** Stable identifier for the chart-type and cost-cache localStorage
   *  keys. Different pages MUST pass different ids so their option
   *  preferences and cached data don't collide. */
  chartId: string
  /** Header title — e.g. "User Consumption Cost Over Time". */
  title: string
  /** Lead-in sentence for the description block above the chart. */
  description: string
  /** Lowercase singular noun for the entity ("user", "role"). Used
   *  inside copy like "Loading user-cost trend…". */
  entityLabelSingular?: string
  /** Lowercase plural noun for the entity ("users", "roles"). Shown
   *  in "Top N <plural>". */
  entityLabelPlural?: string
}>(), {
  loading: false,
  entityLabelSingular: 'user',
  entityLabelPlural: 'users',
})

// Persisted preferences. Defaults match the originally shipped
// behavior so an existing user without a stored preference sees the
// same chart they did before.
const { chartType, stacked, filled, dataLabels, topN } = useChartTypePreference(props.chartId, {
  defaultType: 'curve',
  defaultStacked: true,
  defaultFilled: true,
  defaultDataLabels: false,
  defaultTopN: 10,
})

// Single-instance legend click handler — single click toggles, double
// click isolates that series, double-click again on the isolated
// series restores all.
const onLegendClick = createIsolatingLegendClickHandler()

// ---------------------------------------------------------------------------
// Pivot — long-form (day × entity) rows → wide-form (day → per-entity totals).
// ---------------------------------------------------------------------------

const totalsByEntity = computed<Map<string, number>>(() => {
  const m = new Map<string, number>()
  for (const r of props.rows) {
    m.set(r.entity, (m.get(r.entity) || 0) + (Number(r.cost) || 0))
  }
  return m
})

const totalInRange = computed<number>(() => {
  let total = 0
  for (const v of totalsByEntity.value.values()) total += v
  return total
})

const topEntities = computed<string[]>(() => {
  return [...totalsByEntity.value.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN.value)
    .map(e => e[0])
})

const days = computed<string[]>(() => {
  const set = new Set<string>()
  for (const r of props.rows) set.add(r.day)
  return [...set].sort()
})

const seriesByEntity = computed<Map<string, number[]>>(() => {
  const series = new Map<string, number[]>()
  const dayIndex = new Map(days.value.map((d, i) => [d, i]))
  const top = new Set(topEntities.value)
  const len = days.value.length

  for (const u of topEntities.value) series.set(u, new Array(len).fill(0))
  series.set('Other', new Array(len).fill(0))

  for (const r of props.rows) {
    const idx = dayIndex.get(r.day)
    if (idx === undefined) continue
    const bucket = top.has(r.entity) ? r.entity : 'Other'
    const arr = series.get(bucket)!
    arr[idx] += Number(r.cost) || 0
  }

  const other = series.get('Other')!
  if (other.every(v => v === 0)) series.delete('Other')

  return series
})

// ---------------------------------------------------------------------------
// Palette + dataset builders.
//   Filled mapping for line/curve mode:
//     filled = true  → stacked area (the original look)
//     filled = false → overlapping comparison lines (no fill, no stacking)
//   Stacking for bar mode is controlled by the dedicated `stacked` flag.
// ---------------------------------------------------------------------------

const PALETTE: { border: string, bg: string, bgSolid: string }[] = [
  { border: 'rgb(139, 92, 246)',  bg: 'rgba(139, 92, 246, 0.30)',  bgSolid: 'rgba(139, 92, 246, 0.85)' },
  { border: 'rgb(59, 130, 246)',  bg: 'rgba(59, 130, 246, 0.30)',  bgSolid: 'rgba(59, 130, 246, 0.85)' },
  { border: 'rgb(6, 182, 212)',   bg: 'rgba(6, 182, 212, 0.30)',   bgSolid: 'rgba(6, 182, 212, 0.85)' },
  { border: 'rgb(20, 184, 166)',  bg: 'rgba(20, 184, 166, 0.30)',  bgSolid: 'rgba(20, 184, 166, 0.85)' },
  { border: 'rgb(34, 197, 94)',   bg: 'rgba(34, 197, 94, 0.30)',   bgSolid: 'rgba(34, 197, 94, 0.85)' },
  { border: 'rgb(234, 179, 8)',   bg: 'rgba(234, 179, 8, 0.30)',   bgSolid: 'rgba(234, 179, 8, 0.85)' },
  { border: 'rgb(249, 115, 22)',  bg: 'rgba(249, 115, 22, 0.30)',  bgSolid: 'rgba(249, 115, 22, 0.85)' },
  { border: 'rgb(244, 63, 94)',   bg: 'rgba(244, 63, 94, 0.30)',   bgSolid: 'rgba(244, 63, 94, 0.85)' },
  { border: 'rgb(168, 85, 247)',  bg: 'rgba(168, 85, 247, 0.30)',  bgSolid: 'rgba(168, 85, 247, 0.85)' },
  { border: 'rgb(156, 163, 175)', bg: 'rgba(156, 163, 175, 0.30)', bgSolid: 'rgba(156, 163, 175, 0.85)' },
]

const chartData = computed(() => {
  if (!props.rows || props.rows.length === 0) return null
  const series = seriesByEntity.value
  const orderedNames = [...topEntities.value, ...(series.has('Other') ? ['Other'] : [])]
  const isBar = chartType.value === 'bar'
  const isLine = chartType.value === 'line'

  const datasets = orderedNames.map((name, i) => {
    const palette = name === 'Other'
      ? PALETTE[PALETTE.length - 1]
      : PALETTE[i % (PALETTE.length - 1)]

    if (isBar) {
      return {
        label: name,
        data: series.get(name) || [],
        backgroundColor: palette.bgSolid,
        borderColor: palette.border,
        borderWidth: 0,
        borderRadius: 3,
      }
    }
    return {
      label: name,
      data: series.get(name) || [],
      backgroundColor: filled.value ? palette.bg : palette.bgSolid,
      borderColor: palette.border,
      borderWidth: filled.value ? 1.5 : 2,
      fill: filled.value,
      tension: isLine ? 0 : 0.3,
      pointRadius: filled.value ? 0 : 2.5,
      pointHoverRadius: 4,
    }
  })
  return { labels: days.value, datasets }
})

const chartOptions = computed(() => {
  const isBar = chartType.value === 'bar'
  const stackBoth = isBar ? stacked.value : filled.value

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: isBar && (props.rows?.length ?? 0) > 200 ? 0 : 400 },
    layout: { padding: dataLabels.value ? { top: 18 } : 0 },
    plugins: {
      legend: {
        position: 'top' as const,
        onClick: onLegendClick,
        labels: {
          color: '#9CA3AF',
          usePointStyle: true,
          padding: 12,
          boxWidth: 8,
          font: { size: 11 },
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(17, 24, 39, 0.92)',
        titleColor: '#F3F4F6',
        bodyColor: '#D1D5DB',
        borderColor: 'rgba(139, 92, 246, 0.5)',
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (ctx: any) => {
            const label = ctx.dataset.label || ''
            return `${label}: $${(ctx.parsed.y as number).toFixed(2)}`
          },
          footer: (items: any[]) => {
            let total = 0
            for (const it of items) total += (it.parsed.y as number) || 0
            return `Total: $${total.toFixed(2)}`
          },
        },
      },
    },
    scales: {
      x: {
        stacked: stackBoth,
        grid: { color: 'rgba(156, 163, 175, 0.08)' },
        ticks: { color: '#9CA3AF', maxRotation: 0, autoSkip: true, autoSkipPadding: 16 },
      },
      y: {
        stacked: stackBoth,
        grid: { color: 'rgba(156, 163, 175, 0.08)' },
        ticks: {
          color: '#9CA3AF',
          callback: (value: any) => '$' + value,
        },
      },
    },
    interaction: { mode: 'nearest' as const, axis: 'x' as const, intersect: false },
  }
})

// ---------------------------------------------------------------------------
// Inline data-labels plugin.
// ---------------------------------------------------------------------------
const MIN_LABEL_VALUE = 0.5

const dataLabelsPlugin = {
  id: 'pulsar-data-labels',
  afterDatasetsDraw(chart: any) {
    if (!dataLabels.value) return
    const ctx = chart.ctx as CanvasRenderingContext2D
    if (!ctx) return
    ctx.save()
    ctx.font = '600 10px ui-sans-serif, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillStyle = chart.options?.color || '#374151'

    chart.data.datasets.forEach((dataset: any, di: number) => {
      const meta = chart.getDatasetMeta(di)
      if (meta.hidden) return
      meta.data.forEach((element: any, i: number) => {
        const raw = Number(dataset.data[i])
        if (!Number.isFinite(raw) || Math.abs(raw) < MIN_LABEL_VALUE) return
        const { x, y } = element.getCenterPoint?.() || { x: element.x, y: element.y }
        const isBarElement = element.constructor?.name === 'BarElement'
        const labelY = isBarElement ? y + 12 : y - 6
        ctx.fillText(`$${raw.toFixed(raw >= 100 ? 0 : 2)}`, x, labelY)
      })
    })
    ctx.restore()
  },
}

// ---------------------------------------------------------------------------
// CSV export. Long-form rows (day, entity, cost) so external tools
// see the source of truth — the chart's top-N + Other bucketing is a
// presentation choice that shouldn't leak into the export.
// ---------------------------------------------------------------------------
function exportCsv() {
  if (!props.rows.length) return
  const header = ['day', props.entityLabelSingular, 'cost'].join(',')
  const lines = props.rows.map((r) => {
    const safeEntity = `"${String(r.entity ?? '').replace(/"/g, '""')}"`
    const cost = Number.isFinite(Number(r.cost)) ? Number(r.cost) : 0
    return [r.day, safeEntity, cost].join(',')
  })
  const csv = [header, ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const today = new Date().toISOString().slice(0, 10)
  a.download = `${props.chartId}-${today}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
</script>
